import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { TILE_URL, TILE_OPTIONS, DEFAULT_CENTER, DEFAULT_ZOOM } from '../map/mapConfig.js';
import '../map/map.css'; // reuse `.map-pane` (full-bleed) — not MapShell/MapController (see below)

interface ViewerLatest {
  lat: number;
  lon: number;
}

interface ViewerMapProps {
  track: [number, number][];
  latest: ViewerLatest | null;
}

/**
 * Deliberately NOT the app's MapController/MapShell (spec 15 viewer): that
 * class subscribes to the DEVICE'S real posStore/GPS to draw own-ship and
 * drive follow mode — wiring it into a page a family member opens on their
 * own phone/laptop would show THEIR position as "own ship", which is wrong
 * and a privacy footgun. This is a minimal, read-only render of the shared
 * boat's track + last position: base chart, one polyline, one marker.
 */
export function ViewerMap({ track, latest }: ViewerMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const trackLineRef = useRef<L.Polyline | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
      fadeAnimation: false,
    });
    L.tileLayer(TILE_URL, TILE_OPTIONS).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      trackLineRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (track.length >= 2) {
      const accent =
        getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#d64a2b';
      if (!trackLineRef.current) {
        trackLineRef.current = L.polyline(track, { color: accent, weight: 3, opacity: 0.9 }).addTo(map);
      } else {
        trackLineRef.current.setLatLngs(track);
        trackLineRef.current.setStyle({ color: accent });
      }
    }

    if (latest) {
      const latlng: [number, number] = [latest.lat, latest.lon];
      if (!markerRef.current) {
        markerRef.current = L.marker(latlng, {
          icon: L.divIcon({
            className: 'viewer-boat',
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            html: '<div class="viewer-boat__dot"></div>',
          }),
          interactive: false,
          keyboard: false,
        }).addTo(map);
      } else {
        markerRef.current.setLatLng(latlng);
      }
    }

    if (!fittedRef.current) {
      if (track.length >= 2) {
        map.fitBounds(L.polyline(track).getBounds(), { padding: [36, 96] });
        fittedRef.current = true;
      } else if (latest) {
        map.setView([latest.lat, latest.lon], 14);
        fittedRef.current = true;
      }
    }
  }, [track, latest]);

  return <div ref={containerRef} className="map-pane" aria-label="Sjøkart" />;
}
